// Layout engine — pure function that computes SVG positions for the timeline grid.
// Ported from journey-a-timeline.jsx computeLayout() (L414-473).

import type { LaneDef, FrameDef } from '../../types/index.ts';

/** Resolve whether a lane is a "context" lane (not a branch lane). */
function isContextLane(lane: LaneDef): boolean {
  if (lane.isBranch !== undefined) return !lane.isBranch;
  return lane.group === 'context';
}

/** Get the lane group key for branch path matching. */
function laneGroupKey(lane: LaneDef): string {
  return lane.group ?? lane.id;
}

// ─── Layout Constants (from journey-a-timeline.jsx L390-396) ───
export const LANE_LABEL_W = 165;
export const FRAME_W = 195;
export const MIN_LANE_H = 56;
export const NODE_BOX_H = 30;
export const NODE_GAP = 4;
export const NODE_PAD = 6;
export const HEADER_H = 52;

export interface NodePosition {
  x: number;
  y: number;
  w: number;
  h: number;
  laneId: string;
}

export interface LayoutResult {
  visibleLanes: LaneDef[];
  visibleFrames: FrameDef[];
  laneHeights: Record<string, number>;
  laneYStart: Record<string, number>;
  nodePositions: Record<string, NodePosition>;
  totalHeight: number;
  totalWidth: number;
}

/**
 * Compute the full SVG layout from topology data and active branch paths.
 * Pure function — no React, no Redux, easily testable.
 */
export function computeLayout(
  allLanes: LaneDef[],
  allFrames: FrameDef[],
  activePaths: Set<string>,
): LayoutResult {
  // ── Filter visible lanes ──
  const visibleLanes = allLanes.filter((l) => isContextLane(l) || activePaths.has(laneGroupKey(l)));

  // ── Filter visible frames ──
  const visibleFrames = allFrames.filter((frame) => {
    return frame.nodes.some((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return false;
      if (isContextLane(lane)) {
        return !n.path || activePaths.has(n.path);
      }
      return activePaths.has(laneGroupKey(lane));
    });
  });

  // ── Build lane index ──
  const laneIdx: Record<string, number> = {};
  visibleLanes.forEach((l, i) => {
    laneIdx[l.id] = i;
  });

  // ── Compute max node count per lane across all frames ──
  const laneMaxNodes: Record<string, number> = {};
  visibleLanes.forEach((l) => {
    laneMaxNodes[l.id] = 1;
  });

  visibleFrames.forEach((frame) => {
    const countByLane: Record<string, number> = {};
    frame.nodes.forEach((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return;
      if (isContextLane(lane) && n.path && !activePaths.has(n.path)) return;
      if (!isContextLane(lane) && !activePaths.has(laneGroupKey(lane))) return;
      countByLane[n.lane] = (countByLane[n.lane] || 0) + 1;
    });
    Object.entries(countByLane).forEach(([lid, cnt]) => {
      if (cnt > (laneMaxNodes[lid] || 0)) laneMaxNodes[lid] = cnt;
    });
  });

  // ── Compute lane heights and Y positions ──
  const laneHeights: Record<string, number> = {};
  const laneYStart: Record<string, number> = {};
  let cumY = HEADER_H;

  visibleLanes.forEach((l) => {
    const maxN = laneMaxNodes[l.id] || 1;
    const h = Math.max(MIN_LANE_H, maxN * (NODE_BOX_H + NODE_GAP) + NODE_PAD * 2);
    laneHeights[l.id] = h;
    laneYStart[l.id] = cumY;
    cumY += h;
  });

  // ── Compute node positions ──
  const nodePositions: Record<string, NodePosition> = {};

  visibleFrames.forEach((frame, fi) => {
    const nodesInLane: Record<string, typeof frame.nodes> = {};

    frame.nodes.forEach((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return;
      if (isContextLane(lane) && n.path && !activePaths.has(n.path)) return;
      if (!isContextLane(lane) && !activePaths.has(laneGroupKey(lane))) return;
      if (!nodesInLane[n.lane]) nodesInLane[n.lane] = [];
      nodesInLane[n.lane].push(n);
    });

    Object.entries(nodesInLane).forEach(([laneId, nodes]) => {
      const lh = laneHeights[laneId];
      const totalH = nodes.length * NODE_BOX_H + (nodes.length - 1) * NODE_GAP;
      const startY = laneYStart[laneId] + (lh - totalH) / 2;

      nodes.forEach((n, ni) => {
        nodePositions[n.id] = {
          x: fi * FRAME_W,
          y: startY + ni * (NODE_BOX_H + NODE_GAP),
          w: FRAME_W - 12,
          h: NODE_BOX_H,
          laneId: n.lane,
        };
      });
    });
  });

  return {
    visibleLanes,
    visibleFrames,
    laneHeights,
    laneYStart,
    nodePositions,
    totalHeight: cumY,
    totalWidth: visibleFrames.length * FRAME_W + 20,
  };
}
